/**
 * Capability-map hooks.
 *
 * The server tells the client which actions each provider supports
 * (`{capabilities: {gmail: {action: boolean}, outlook: {…}, imap: {…}},
 * generatedAt}`). The frontend reads this to hide UI affordances for
 * unsupported actions BEFORE firing a request that would 422 with
 * `UNSUPPORTED_ON_PROVIDER`.
 *
 * Three exports:
 *   - `capabilityMapQueryOptions` — query options for
 *     `queryClient.prefetchQuery(...)` on auth success so the map is warm
 *     before any destructive UI renders.
 *   - `useCapabilityMap()` — internal TanStack hook returning the envelope
 *     (+ loading/error). Callers normally consume `useProviderCapability`.
 *   - `useProviderCapability(provider, action)` — returns
 *     `{supported, reason?}`. Defaults differ by action class while the
 *     map loads or errors:
 *       • Read actions → `{supported: true}` (false-positive cost: a
 *         request that may 422 — recoverable).
 *       • Destructive actions → `{supported: false, reason:
 *         'CAPABILITY_MAP_LOADING' | 'CAPABILITY_MAP_UNAVAILABLE'}`
 *         (false-negative cost: a disabled Send button for ≤30s).
 *
 * `reason` is a bounded enum string — safe for UI copy keys and analytics.
 */

import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  DESTRUCTIVE_ACTIONS,
  type CapabilitiesEnvelope,
  type CorrespondenceAction,
  type Provider,
} from '@/lib/correspondence/contracts';

import { invokeCorrespondenceApi, shouldRetryCorrespondenceError } from './use-correspondence-api';

import type { CorrespondenceApiError } from '@/lib/errors/correspondence-api-error';

/**
 * Session-token accessor shared across the capability-map hooks and the
 * `prefetchCapabilityMap` helper. AuthProvider registers its ref reader on
 * mount (`setSessionTokenGetter`); both `useCapabilityMap`'s useQuery path
 * AND the prefetch path consult the same token through `getSessionToken`.
 *
 * This replaces the previous design where only the prefetch path had the
 * guard — `useCapabilityMap`'s `refetchOnMount: 'always'` /
 * `refetchOnWindowFocus: true` could fire the unguarded queryFn during an
 * auth transition and write stale-user data into the post-sign-in cache.
 *
 * Default getter returns 0 (any bump against this is detectable); the
 * AuthProvider replaces it on mount to read the live token ref.
 */
let currentSessionTokenGetter: () => number = () => 0;

export function setSessionTokenGetter(getter: () => number): void {
  currentSessionTokenGetter = getter;
}

export function getSessionToken(): number {
  return currentSessionTokenGetter();
}

/**
 * Reason codes the hook surfaces back to callers. Bounded enum so UI copy
 * maps can dispatch off a stable key (not a freeform error message).
 */
export type CapabilityReason =
  | 'UNSUPPORTED_ON_PROVIDER'
  | 'CAPABILITY_MAP_LOADING'
  | 'CAPABILITY_MAP_UNAVAILABLE'
  /** `useAccountCapability` could not resolve the account's provider. */
  | 'ACCOUNT_UNRESOLVED';

export interface ProviderCapability {
  readonly supported: boolean;
  readonly reason?: CapabilityReason;
}

export const CAPABILITY_MAP_QUERY_KEY = ['correspondence', 'capabilities'] as const;

/**
 * Builds capability-map query options with the session-token guard baked
 * into the queryFn. Both `useCapabilityMap` (via `useQuery`) and
 * `prefetchCapabilityMap` (via `prefetchQuery`) share this factory, so the
 * guard covers every entry point that could write into the cache —
 * prefetch, `refetchOnMount: 'always'`, `refetchOnWindowFocus: true`.
 *
 * Previously only the prefetch path was guarded; a `useCapabilityMap` mount
 * during an auth transition could fire an unguarded refetch and write
 * stale-user data into the post-sign-in cache.
 *
 * Timings:
 *   - `staleTime: 30 min` — capability is static within an edge-function
 *     worker lifetime; a deploy spawns fresh workers and invalidates on
 *     next refetch anyway.
 *   - `gcTime: 24h` (finite, not `Infinity`) — `persistQueryClient`
 *     JSON-serialises the cache and `Infinity` would round-trip as `null`
 *     → TanStack default (5 min) on rehydrate. The capability map is
 *     excluded from persistence entirely (see App.tsx `shouldDehydrateQuery`),
 *     but this keeps gcTime expressive in case persistence is re-enabled later.
 *   - `refetchOnWindowFocus: true` + `refetchOnMount: 'always'` — closes
 *     the "user had tab open during deploy" staleness window.
 *   - Retry policy: delegated to `shouldRetryCorrespondenceError`.
 */
function buildCapabilityMapQueryOptions(
  queryClient: QueryClient,
): UseQueryOptions<CapabilitiesEnvelope, CorrespondenceApiError> {
  return {
    queryKey: CAPABILITY_MAP_QUERY_KEY,
    queryFn: async () => await guardedFetchCapabilityMap(queryClient),
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    retry: shouldRetryCorrespondenceError,
  };
}

/**
 * Session-token-guarded queryFn. Used by BOTH `useCapabilityMap` and
 * `prefetchCapabilityMap`. If the session token changes between start and
 * resolve, refuses the write and schedules a conditional removeQueries
 * microtask (only removes if the slot still holds our error state — a
 * concurrent successful write is left alone).
 *
 * Rationale: Supabase SDK v2 does not accept an `AbortSignal` on
 * `functions.invoke`, so a signout mid-flight cannot cancel the HTTP
 * round-trip. Without this guard, the delayed response writes stale-user
 * data into the freshly-cleared (or user-B-populated) cache.
 */
async function guardedFetchCapabilityMap(queryClient: QueryClient): Promise<CapabilitiesEnvelope> {
  const startToken = getSessionToken();
  const result = await invokeCorrespondenceApi('get_capabilities', {});
  if (getSessionToken() !== startToken) {
    queueMicrotask(() => {
      const state = queryClient.getQueryState(CAPABILITY_MAP_QUERY_KEY);
      if (state?.status === 'error') {
        queryClient.removeQueries({ queryKey: CAPABILITY_MAP_QUERY_KEY });
      }
    });
    throw new Error('capability-map fetch aborted: session changed');
  }
  return result;
}

/**
 * Back-compat alias retained so `auth-context.tsx` and existing tests that
 * reference the old `capabilityMapQueryOptions` constant keep compiling.
 * Prefer `buildCapabilityMapQueryOptions(queryClient)` for new callers —
 * the `queryClient` argument is required for the guard's removeQueries
 * cleanup path.
 *
 * @deprecated Use `buildCapabilityMapQueryOptions(queryClient)` directly.
 *   Retained to avoid breaking Phase 4 consumers during the 325 rollout.
 */
export const capabilityMapQueryOptions: UseQueryOptions<
  CapabilitiesEnvelope,
  CorrespondenceApiError
> = {
  queryKey: CAPABILITY_MAP_QUERY_KEY,
  queryFn: async () => await invokeCorrespondenceApi('get_capabilities', {}),
  staleTime: 30 * 60 * 1000,
  gcTime: 24 * 60 * 60 * 1000,
  refetchOnWindowFocus: true,
  refetchOnMount: 'always',
  retry: shouldRetryCorrespondenceError,
};

/**
 * Fires a guarded prefetch. Called from `auth-context.tsx` on `SIGNED_IN`
 * so the capability map warms before any destructive UI renders. Kicks off
 * without awaiting — callers rely on fire-and-forget semantics.
 *
 * The `getSessionToken` argument is retained for test ergonomics (a test
 * can pass a local token getter without mutating module state) but MUST
 * also be registered globally via `setSessionTokenGetter` so the
 * `useCapabilityMap` refetch path sees the same token — AuthProvider does
 * this on mount.
 */
export function prefetchCapabilityMap(
  queryClient: QueryClient,
  // Kept for test injection; production callers should rely on the
  // module-level getter registered by AuthProvider.
  _getSessionToken?: () => number,
): void {
  if (_getSessionToken !== undefined) {
    setSessionTokenGetter(_getSessionToken);
  }
  void queryClient.prefetchQuery(buildCapabilityMapQueryOptions(queryClient)).catch(() => {
    // Swallowed; the useQuery consumer handles missing cache via
    // optimistic-or-conservative defaults per action class.
  });
}

export function useCapabilityMap(): {
  envelope?: CapabilitiesEnvelope;
  isLoading: boolean;
  isError: boolean;
} {
  const queryClient = useQueryClient();
  const options = useMemo(() => buildCapabilityMapQueryOptions(queryClient), [queryClient]);
  const { data, isLoading, isError } = useQuery(options);
  return { envelope: data, isLoading, isError };
}

/**
 * Resolves whether `action` is supported on `provider`. Uses the cached
 * capability map when available; otherwise applies the optimistic-or-
 * conservative default appropriate for the action class.
 */
export function useProviderCapability(
  provider: Provider,
  action: CorrespondenceAction,
): ProviderCapability {
  const { envelope, isLoading, isError } = useCapabilityMap();

  return useMemo(() => {
    if (envelope) {
      const supported = envelope.capabilities[provider]?.[action] ?? false;
      if (supported) {
        return { supported: true };
      }
      return { supported: false, reason: 'UNSUPPORTED_ON_PROVIDER' };
    }

    const isDestructive = DESTRUCTIVE_ACTIONS.has(action);
    if (isLoading) {
      return isDestructive
        ? { supported: false, reason: 'CAPABILITY_MAP_LOADING' }
        : { supported: true };
    }
    if (isError) {
      return isDestructive
        ? { supported: false, reason: 'CAPABILITY_MAP_UNAVAILABLE' }
        : { supported: true };
    }
    // Envelope absent, not loading, not errored — e.g. very first tick
    // before the query starts. Treat identically to the loading branch.
    return isDestructive
      ? { supported: false, reason: 'CAPABILITY_MAP_LOADING' }
      : { supported: true };
  }, [envelope, isLoading, isError, provider, action]);
}
